<script setup lang="ts">
import ReportTemplateMenu from '@/components/createdUi/ReportTemplateMenu.vue'
import { improvementReportFilters } from '@/modules/improvements/utils/reportFilters'

/*
* основной интерфейс для системы предложений по улучшениям
*
* Основные функции:
* - Создание новых предложений по улучшениям с возможностью анонимной публикации
* - Просмотр списка предложений с пагинацией и бесконечной подгрузкой
* - Фильтрация предложений по статусу (новые, отклоненные, реализованные) и авторству
* - Управление активными фильтрами через систему чипов
* - Интеграция с компонентами карточек предложений и ответов
*
* Особенности:
* - Реализация бесконечного скролла через Intersection Observer
* - Система подтверждения при закрытии формы с несохраненными данными
* - Поддержка анонимных предложений
* - Адаптивный дизайн для мобильных устройств
*/

// Импорты Vuetify
import { useDisplay } from 'vuetify/framework'
import { VIconBtn } from 'vuetify/labs/VIconBtn'

// Импорты хранилищ
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useImprovementsStore } from '@/modules/improvements/stores/improvementsStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'

// Импорты компонентов
import CreateSuggestionModal from '@/modules/improvements/components/CreateSuggestionModal.vue'
import ImprovementFormModal from '@/modules/improvements/components/ImprovementFormModal.vue'
import SuggestionCard from '@/modules/improvements/components/SuggestionCard.vue'
import SuggestionCardSkeleton from '@/modules/improvements/components/SuggestionCardSkeleton.vue'

import { ERouteNames } from '@/router/ERouteNames'
import { onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import OnboardingLaunchButton from '@/modules/onboarding/components/OnboardingLaunchButton.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingDemoRows } from '@/modules/onboarding/composables/useOnboardingDemoRows'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'
import { IMPROVEMENTS_ANCHORS, IMPROVEMENTS_CONTEXTS } from '@/modules/onboarding/scenarios/improvements/anchors'
import { IMPROVEMENTS_FORM_DEMO_INDEX } from '@/modules/onboarding/scenarios/improvements/adapter'
import { ONBOARDING_DEMO_QUERY } from '@/modules/onboarding/utils/demoRoute'
import {
  PPU_AGGREGATED_STATUS_KEYS,
  normalizeAggregatedStatusKey,
  normalizeStageKey,
} from '@/modules/improvements/utils/ppuStatusHelper'

// Инициализация утилит отображения Vuetify
const { xs, smAndDown } = useDisplay()

// Инициализация хранилищ и роутера
const route = useRoute()
const router = useRouter()
const improvementsStore = useImprovementsStore()
const permissionsStore = usePermissionsStore()
const feedbackStore = useFeedbackStore()

// Реактивные состояния управления UI
const showSuggestionsFilter = ref(false)

const isCreateSuggestionModalOpen = ref(false)
const isDetailModalOpen = ref(false)
const selectedSuggestionId = ref<number | null>(null)

function openCreateSuggestionModal() {
  isCreateSuggestionModalOpen.value = true
}

function syncFiltersToQuery() {
  const query: Record<string, any> = { ...route.query }

  if (improvementsStore.filters.status?.length) {
    query.status = improvementsStore.filters.status.join(',')
  } else {
    delete query.status
  }

  if (improvementsStore.filters.stages?.length) {
    query.stages = improvementsStore.filters.stages.join(',')
  } else {
    delete query.stages
  }
  delete query.statuses

  if (improvementsStore.filters.mine) {
    query.mine = 'true'
  } else {
    delete query.mine
  }

  if (improvementsStore.filters.my_department) {
    query.my_department = 'true'
  } else {
    delete query.my_department
  }

  if (improvementsStore.filters.requires_action) {
    query.requires_action = 'true'
  } else {
    delete query.requires_action
  }

  router.replace({ query }).catch(() => {})
}

function readFiltersFromQuery() {
  const q = route.query
  const statusFromQuery = q.status ? String(q.status).split(',') : []
  const stagesQuery = q.stages || q.statuses
  const stagesFromQuery = stagesQuery ? String(stagesQuery).split(',') : []
  const split = splitDraftFilterValues([...statusFromQuery, ...stagesFromQuery])
  improvementsStore.filters.status = split.status
  improvementsStore.filters.stages = split.stages
  if (q.mine === 'true') {
    improvementsStore.filters.mine = true
  }
  if (q.my_department === 'true') {
    improvementsStore.filters.my_department = true
  }
  if (q.requires_action === 'true') {
    improvementsStore.filters.requires_action = true
  }
}

function openDetailModal(id: number, query = route.query) {
  selectedSuggestionId.value = id
  isDetailModalOpen.value = true
  if (route.params.id !== String(id)) {
    router.push({ name: ERouteNames.IMPROVEMENT_DETAIL, params: { id }, query })
  }
}

onMounted(() => {
  readFiltersFromQuery()
  if (route.params.id) {
    const parsed = Number(route.params.id)
    if (!isNaN(parsed)) {
      openDetailModal(parsed)
    }
  }
})

/* Онбординг: демо-предложения первыми в ленте и действия, которыми сценарий открывает меню, модалки и страницы */
const onboardingStore = useOnboardingStore()
const { rows: feedItems } = useOnboardingDemoRows('improvement', computed(() => improvementsStore.items))
const onboardingFormDemoId = computed(() => onboardingStore.activeDemoItems[IMPROVEMENTS_FORM_DEMO_INDEX]?.list.id ?? null)
const isActionsMenuOpen = ref<number | undefined>(undefined)

useOnboardingContext({
  names: [IMPROVEMENTS_CONTEXTS.feed],
  scenarioId: 'improvements',
  root: () => document.body,
  actions: {
    openActionsMenu: () => {
      if (smAndDown.value)
        isActionsMenuOpen.value = 0
    },
    openCreateModal: () => { isCreateSuggestionModalOpen.value = true },
    closeCreateModal: () => { isCreateSuggestionModalOpen.value = false },
    openSettings: async () => { await router.push({ name: ERouteNames.IMPROVEMENTS_COMMISSION }) },
    openDemoForm: () => {
      if (onboardingFormDemoId.value !== null)
        openDetailModal(onboardingFormDemoId.value, { ...route.query, [ONBOARDING_DEMO_QUERY]: '1' })
    },
    closeForm: async () => {
      isDetailModalOpen.value = false
      selectedSuggestionId.value = null
      if (route.name === ERouteNames.IMPROVEMENT_DETAIL)
        await router.replace({ name: ERouteNames.IMPROVEMENTS })
    },
  },
})

function onSuggestionCreated() {
  improvementsStore.fetchSuggestions()
}

function onSuggestionUpdated() {
  improvementsStore.fetchSuggestions()
}



// Переменные для управления бесконечным скроллом
const sentinel = ref<HTMLElement | null>(null)
let observer: IntersectionObserver | null = null
let isLoading = false

const AGGREGATED_FILTER_VALUES = new Set<string>(PPU_AGGREGATED_STATUS_KEYS)

const statusOptions = [
  { text: 'Новые', value: 'new' },
  { text: 'В работе', value: 'in_progress' },
  { text: 'На доработке', value: 'under_revision' },
  { text: 'Принято', value: 'accepted' },
  { text: 'Отклонено', value: 'rejected' },
  { text: 'Отложено', value: 'archived' },
]

function splitDraftFilterValues(values: string[]) {
  const normalized = values.map((value) => {
    const key = normalizeStageKey(normalizeAggregatedStatusKey(value))
    if (key === 'returned_to_author')
      return 'under_revision'
    return key
  })

  return {
    status: normalized.filter(value => AGGREGATED_FILTER_VALUES.has(value)),
    stages: [] as string[],
  }
}

type TFilterAuthor = 'all' | 'my' | 'department'

const draftAuthor = ref<TFilterAuthor>('all')
const draftStatuses = ref<string[]>([])

const hasActiveFilters = computed(() => {
  return (
    (improvementsStore.filters.status?.length ?? 0) > 0
    || (improvementsStore.filters.stages?.length ?? 0) > 0
    || improvementsStore.filters.mine
    || improvementsStore.filters.my_department
    || Boolean(improvementsStore.filters.requires_action)
  )
})

const showListLoader = computed(() =>
  improvementsStore.isLoading && !feedbackStore.isGlobalLoading,
)

const activeFilterChips = computed(() => {
  const chips: Array<{ label: string; type: string; value?: string }> = []

  ;[...improvementsStore.filters.status, ...improvementsStore.filters.stages].forEach(status => {
    const statusOption = statusOptions.find(opt => opt.value === status)
    if (statusOption) {
      chips.push({
        label: `Статус: ${statusOption.text}`,
        type: 'status',
        value: status,
      })
    }
  })

  if (improvementsStore.filters.mine) {
    chips.push({
      label: 'Мои',
      type: 'author',
      value: 'my',
    })
  }

  if (improvementsStore.filters.my_department) {
    chips.push({
      label: 'Мое подразделение',
      type: 'author',
      value: 'department',
    })
  }

  if (improvementsStore.filters.requires_action) {
    chips.push({
      label: 'Требует действия',
      type: 'requires_action',
      value: 'requires_action',
    })
  }

  return chips
})

const isRequiresActionFilterActive = computed(() => Boolean(improvementsStore.filters.requires_action))

function toggleRequiresActionFilter() {
  improvementsStore.filters.requires_action = isRequiresActionFilterActive.value ? undefined : true
  triggerServerFilter()
}

const getFilterIcon = (isActive: boolean) => isActive ? 'ri-filter-fill' : 'ri-filter-2-line'

const triggerServerFilter = () => {
  syncFiltersToQuery()
  improvementsStore.nextCursor = null
  improvementsStore.fetchSuggestions()
}

function syncDraftFromStore() {
  if (improvementsStore.filters.mine) {
    draftAuthor.value = 'my'
  } else if (improvementsStore.filters.my_department) {
    draftAuthor.value = 'department'
  } else {
    draftAuthor.value = 'all'
  }
  draftStatuses.value = [...improvementsStore.filters.status, ...improvementsStore.filters.stages]
}

watch(showSuggestionsFilter, open => {
  if (open) {
    syncDraftFromStore()
  }
})

function cancelFilter() {
  showSuggestionsFilter.value = false
}

function applyFilter() {
  improvementsStore.filters.mine = draftAuthor.value === 'my'
  improvementsStore.filters.my_department = draftAuthor.value === 'department'
  const split = splitDraftFilterValues(draftStatuses.value)
  improvementsStore.filters.status = split.status
  improvementsStore.filters.stages = split.stages
  triggerServerFilter()
  showSuggestionsFilter.value = false
}

const removeFilter = (chip: { label: string; type: string; value?: string }) => {
  switch (chip.type) {
    case 'status':
      improvementsStore.filters.status = improvementsStore.filters.status.filter(
        status => status !== chip.value,
      )
      break
    case 'stage':
      improvementsStore.filters.stages = improvementsStore.filters.stages.filter(
        stage => stage !== chip.value,
      )
      break
    case 'author':
      if (chip.value === 'my') {
        improvementsStore.filters.mine = false
      } else if (chip.value === 'department') {
        improvementsStore.filters.my_department = false
      }
      break
    case 'requires_action':
      improvementsStore.filters.requires_action = undefined
      break
  }

  triggerServerFilter()
}


/**
 * Настройка Intersection Observer для бесконечного скролла
 *
 * Назначение:
 * - Создает и настраивает наблюдатель для автоматической подгрузки данных
 * - Отслеживает появление sentinel-элемента в области видимости
 * - Загружает следующую порцию данных при необходимости
 *
 * Условия работы:
 * - Имеется курсор для следующей страницы (nextCursor)
 * - Sentinel-элемент присутствует в DOM
 *
 * Параметры наблюдателя:
 * - rootMargin: '150px' - предзагрузка за 150px до появления элемента
 * - threshold: 0.1 - срабатывает при 10% видимости элемента
 */
function setupObserver() {
  // не создаём observer если нет курсора
  if (!improvementsStore.nextCursor) return
  const el = sentinel.value
  if (!el) return

  // отключаем старый, если есть
  if (observer) {
    observer.disconnect()
    observer = null
  }

  observer = new IntersectionObserver(async entries => {
    const entry = entries[0]
    if (entry?.isIntersecting && !isLoading && improvementsStore.nextCursor) {
      isLoading = true
      try {
        await improvementsStore.fetchSuggestions(improvementsStore.nextCursor)
      } finally {
        isLoading = false
        // отключаем если больше нет курсора
        if (improvementsStore.nextCursor && observer) {
          observer.disconnect()
          observer = null
        }
      }
    }
  }, { root: null, rootMargin: '150px', threshold: 0.1 })

  observer.observe(el)
}

/**
 * Отключение Intersection Observer
 *
 * Назначение:
 * - Корректно отключает наблюдатель при размонтировании компонента
 * - Освобождает ресурсы и предотвращает утечки памяти
 */
function disconnectObserver() {
  if (observer) {
    observer.disconnect()
    observer = null
  }
}

/**
 * Вотчер для отслеживания изменения курсора пагинации
 *
 * Назначение:
 * - Автоматически настраивает наблюдатель при появлении нового курсора
 * - Обеспечивает непрерывную работу бесконечного скролла
 */
watch(() => improvementsStore.nextCursor, () => {
  // если появился курсор —  установить observer
  setupObserver()
})

/**
 * Хук монтирования компонента
 *
 * Выполняет:
 * 1. Загружает начальный список предложений по улучшениям
 * 2. Настраивает Intersection Observer для бесконечного скролла
 * 3. Монтирование скрипта Битрикс 24
 */
onMounted(async () => {
  if (document.getElementById('bitrix24-site-button')) return

  const script = document.createElement('script')
  script.id = 'bitrix24-site-button'
  script.async = true
  script.src
    = `https://cdn-ru.bitrix24.ru/b33863400/crm/site_button/loader_8_p1z21n.js?${
      (Date.now() / 60000) | 0}`

  document.body.appendChild(script) // нужно append в body, иначе не работает

  await improvementsStore.fetchSuggestions()

  // Автостарт только на самой ленте: при прямом заходе в карточку обучение не перебивает работу
  if (route.name === ERouteNames.IMPROVEMENTS)
    void onboardingStore.autoStart('improvements')
})

/**
 * Хук размонтирования компонента
 *
 * Выполняет:
 * 1. Отключает Intersection Observer для освобождения ресурсов
 */
onUnmounted(() => {
  disconnectObserver()
})

/**
 * Хук Vue Router для обработки ухода с маршрута компонента
 *
 * Назначение:
 * - Удаляет скрипт Битрикс24, чтобы виджет не оставался на других страницах
 * - Очищает все DOM-элементы виджета Битрикс24, которые могли быть созданы скриптом
 *
 * Логика работы:
 * 1. Ищет элемент <script> с id 'bitrix24-site-button' и удаляет его из DOM
 * 2. Ищет все элементы виджета с классами '.b24-widget-button-shadow' и '.b24-widget-button-wrapper' и удаляет их
 */
onBeforeRouteLeave(() => {
  // Удаляем скрипт
  const script = document.getElementById('bitrix24-site-button')
  if (script) script.remove()

  // Удаляем все элементы виджета Битрикс
  document.querySelectorAll('.b24-widget-button-shadow, .b24-widget-button-wrapper').forEach(el => el.remove())
})
const templateFilters = computed(() => improvementReportFilters(improvementsStore.filters))
</script>

<template>
  <div id="bitrix-container" class="d-flex flex-column">
    <!-- Основной контейнер -->
    <div class="d-flex flex-column gap-4 mb-4">
      <header style="max-width: 58rem;">
        <!-- Панель инструментов -->
        <div :class="smAndDown ? 'd-flex flex-column gap-2 w-100' : 'd-flex align-center justify-space-between w-100'">
          <VBtn
            :class="{ 'w-100': smAndDown }"
            variant="elevated"
            color="primary"
            height="38"
            prepend-icon="ri-add-line"
            :data-onboarding="IMPROVEMENTS_ANCHORS.feed.create"
            @click="openCreateSuggestionModal"
          >
            Предложить
          </VBtn>

          <div :class="smAndDown ? 'd-flex flex-column gap-2 w-100' : 'd-flex align-center gap-2'">
            <VExpansionPanels
              v-if="smAndDown"
              v-model="isActionsMenuOpen"
              class="w-100"
            >
              <VExpansionPanel>
                <VExpansionPanelTitle class="d-flex align-center py-2 px-4">
                  <div class="d-flex align-center gap-2">
                    <VIcon
                      icon="ri-menu-2-line"
                      size="20"
                    />
                    <span class="text-subtitle-1 font-weight-medium">Меню действий</span>
                  </div>
                </VExpansionPanelTitle>
                <VExpansionPanelText>
                  <div class="d-flex flex-column gap-2 w-100">
                    <VBtn
                      v-if="permissionsStore.modules.improvements?.manage"
                      class="w-100"
                      variant="outlined"
                      color="secondary"
                      prepend-icon="ri-settings-4-line"
                      :data-onboarding="IMPROVEMENTS_ANCHORS.feed.settings"
                      @click="router.push({ name: ERouteNames.IMPROVEMENTS_COMMISSION })"
                    >
                      Настройки
                    </VBtn>

                    <VBtn
                      class="w-100"
                      variant="outlined"
                      color="secondary"
                      prepend-icon="ri-bill-line"
                      :data-onboarding="IMPROVEMENTS_ANCHORS.feed.registry"
                      @click="router.push({ name: ERouteNames.IMPROVEMENTS_REGISTRY })"
                    >
                      Реестр
                    </VBtn>

                    <VBtn
                      class="w-100"
                      variant="outlined"
                      color="secondary"
                      prepend-icon="ri-bookmark-3-line"
                      :data-onboarding="IMPROVEMENTS_ANCHORS.feed.rating"
                      @click="router.push({ name: ERouteNames.IMPROVEMENTS_RATING })"
                    >
                      Рейтинг
                    </VBtn>
                  </div>
                </VExpansionPanelText>
              </VExpansionPanel>
            </VExpansionPanels>

            <div
              v-else
              class="d-flex align-center gap-2"
            >
              <VBtn
                v-if="permissionsStore.modules.improvements?.manage"
                variant="outlined"
                color="secondary"
                prepend-icon="ri-settings-4-line"
                :data-onboarding="IMPROVEMENTS_ANCHORS.feed.settings"
                @click="router.push({ name: ERouteNames.IMPROVEMENTS_COMMISSION })"
              >
                Настройки
              </VBtn>

              <VBtn
                variant="outlined"
                color="secondary"
                prepend-icon="ri-bill-line"
                :data-onboarding="IMPROVEMENTS_ANCHORS.feed.registry"
                @click="router.push({ name: ERouteNames.IMPROVEMENTS_REGISTRY })"
              >
                Реестр
              </VBtn>

              <VBtn
                variant="outlined"
                color="secondary"
                prepend-icon="ri-bookmark-3-line"
                :data-onboarding="IMPROVEMENTS_ANCHORS.feed.rating"
                @click="router.push({ name: ERouteNames.IMPROVEMENTS_RATING })"
              >
                Рейтинг
              </VBtn>
            </div>

            <!-- «Требует действия», отчёты и фильтр одной строкой (на мобилке — «Требует действия» растянута, иконки справа) -->
            <div class="d-flex align-center gap-2" :class="{ 'w-100': smAndDown }">
              <VBtn
                :class="{ 'flex-grow-1': smAndDown }"
                :prepend-icon="getFilterIcon(isRequiresActionFilterActive)"
                variant="outlined"
                color="secondary"
                height="38"
                :data-onboarding="IMPROVEMENTS_ANCHORS.feed.requiresAction"
                @click="toggleRequiresActionFilter"
              >
                Требует действия
              </VBtn>

              <ReportTemplateMenu object-type="improvement" context="collection" :filters="templateFilters" :sort="improvementsStore.currentSortString" variant="toolbar" />

              <VIconBtn
                color="secondary"
                class="rounded-sm"
                :data-onboarding="IMPROVEMENTS_ANCHORS.feed.filter"
                :icon="getFilterIcon(hasActiveFilters)"
                variant="outlined"
                :rounded="false"
                width="38"
                height="38"
                @click="showSuggestionsFilter = !showSuggestionsFilter"
              />

              <OnboardingLaunchButton scenario="improvements" />
            </div>

            <VMenu
              v-model="showSuggestionsFilter"
              :close-on-content-click="false"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <VCard>
                <div class="d-flex flex-column">
                  <span class="text-h5 text-high-emphasis pa-4">Фильтры</span>
                  <VDivider />
                  <div class="d-flex flex-column pa-4 ga-6">
                    <div class="d-flex flex-column ga-2">
                      <span class="text-caption text-secondary text-uppercase">Авторство</span>
                      <VRadioGroup
                        v-model="draftAuthor"
                        class="filter-author-radios"
                        inline
                        hide-details
                        density="compact"
                        color="primary"
                      >
                        <VRadio value="all">
                          <template #label>
                            <span class="text-body-1 text-high-emphasis">Все</span>
                          </template>
                        </VRadio>
                        <VRadio value="my">
                          <template #label>
                            <span class="text-body-1 text-high-emphasis">Мои</span>
                          </template>
                        </VRadio>
                        <VRadio value="department">
                          <template #label>
                            <span class="text-body-1 text-high-emphasis">Мое подразделение</span>
                          </template>
                        </VRadio>
                      </VRadioGroup>
                    </div>
                    <div class="d-flex flex-column ga-2">
                      <span class="text-caption text-secondary text-uppercase">Статус</span>
                      <VCheckbox
                        v-for="status in statusOptions"
                        :key="status.value"
                        v-model="draftStatuses"
                        :value="status.value"
                        hide-details
                        color="primary"
                        density="compact"
                      >
                        <template #label>
                          <span class="text-body-1 text-high-emphasis">{{ status.text }}</span>
                        </template>
                      </VCheckbox>
                    </div>
                  </div>
                  <div
                    class="d-flex ga-2 pa-4"
                    :class="xs ? 'flex-column' : ''"
                  >
                    <VBtn
                      :block="xs"
                      :class="{ 'flex-1-1-0': !xs }"
                      variant="tonal"
                      color="secondary"
                      @click="cancelFilter"
                    >
                      Отмена
                    </VBtn>
                    <VBtn
                      :block="xs"
                      :class="{ 'flex-1-1-0': !xs }"
                      variant="elevated"
                      color="primary"
                      @click="applyFilter"
                    >
                      Применить
                    </VBtn>
                  </div>
                </div>
              </VCard>
            </VMenu>
          </div>
        </div>
      </header>

      <div v-if="activeFilterChips.length" class="d-flex gap-2 flex-wrap">
        <VChip
          v-for="chip in activeFilterChips"
          :key="chip.value"
          closable
          @click:close="removeFilter(chip)"
        >
          {{ chip.label }}
        </VChip>
      </div>
    </div>

    <div
      v-if="showListLoader && !improvementsStore.items.length"
      class="d-flex flex-column gap-2"
      style="max-width: 58rem;"
    >
      <SuggestionCardSkeleton
        v-for="n in 3"
        :key="n"
      />
    </div>

    <div
      v-else-if="feedItems.length"
      class="d-flex flex-column gap-2"
      style="max-width: 58rem;"
    >
      <SuggestionCard
        v-for="item in feedItems"
        :id="item.id"
        :key="item.id"
        :data-onboarding="item.id === onboardingFormDemoId ? IMPROVEMENTS_ANCHORS.feed.demoCard : undefined"
        :title="item.title"
        :text="item.text"
        :status="item.stage || item.workflow_status || 'pending_department_head'"
        :status-display="item.stage_display || item.workflow_status_display"
        :aggregated-status="item.status"
        :aggregated-status-display="item.status_display"
        :answer="item.latest_review_comment?.comment || ''"
        :likes-count="item.likes_count"
        :dislikes-count="item.dislikes_count"
        :created-by="item.created_by ?? null"
        :created-at="item.created_at"
        :requires-action="item.requires_action"
        @clickCard="openDetailModal(item.id)"
      />

      <SuggestionCardSkeleton v-if="showListLoader" />
    </div>

    <div
      v-else-if="!improvementsStore.isLoading && !feedbackStore.isGlobalLoading"
      class="text-subtitle-1 text-medium-emphasis pa-4"
      style="max-width: 58rem;"
    >
      Предложений пока нет...
    </div>



    <div
      v-if="improvementsStore.nextCursor"
      ref="sentinel"
      style="width: 1px; height: 1px; opacity: 0; pointer-events: none;"
      aria-hidden="true"
    />

    <!-- Модалка создания ППУ -->
    <CreateSuggestionModal
      v-model="isCreateSuggestionModalOpen"
      @created="onSuggestionCreated"
    />

    <!-- Модалка карточки ППУ -->
    <ImprovementFormModal
      v-model="isDetailModalOpen"
      :suggestion-id="selectedSuggestionId"
      @updated="onSuggestionUpdated"
    />
  </div>
</template>

<style scoped lang="scss">
.filter-author-radios :deep(.v-selection-control-group) {
  column-gap: 12px;
}
</style>


